import React, { useMemo, useState } from 'react';
import Spinner from '../../../components/common/spinner';
import { Paginator } from '../../../components/common/paginator';
import TagSelectorModal from '../../../components/content-library/tag-selector-modal';
// TODO: Migrate to useUpdateResourceV3 when deprecating v2 resource endpoints (follow-up ticket).
import { Resource, useUpdateResource } from '../../../hooks/useResources';
import { Tag } from '../../../hooks/useTags';
import { EmptyState, TagList, Truncate, formatEnabledFor } from './common';
import { getResourceTagNames } from '../utils/tag-helpers';

export interface ResourceContentTableProps {
  rows: Resource[] | undefined;
  tags: Tag[];
  tagMap: Map<number, Tag>;
  isLoading: boolean;
  count?: number;
  limit: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onShowDetails: (resource: Resource) => void;
}

export const ResourceContentTable: React.FC<ResourceContentTableProps> = ({
  rows = [],
  tags,
  tagMap,
  isLoading,
  count,
  limit,
  currentPage,
  onPageChange,
  onShowDetails,
}) => {
  if (isLoading) {
    return <Spinner />;
  }

  if (!rows.length) {
    return <EmptyState message="No resources found." />;
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left text-xs uppercase text-gray-500">
              <th className="w-56 px-4 py-4">Name</th>
              <th className="w-28 px-4 py-4">Client</th>
              <th className="w-40 px-4 py-4">Enabled For</th>
              <th className="w-20 px-4 py-4">Status</th>
              <th className="w-16 px-4 py-4">Sort</th>
              <th className="w-44 px-4 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((resource) => (
              <ResourceContentRow
                key={resource.id}
                resource={resource}
                tags={tags}
                tagMap={tagMap}
                onShowDetails={onShowDetails}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-end">
        <Paginator count={count ?? 0} limit={limit} currentPage={currentPage} handlePageChange={onPageChange} />
      </div>
    </div>
  );
};

interface ResourceContentRowProps {
  resource: Resource;
  tags: Tag[];
  tagMap: Map<number, Tag>;
  onShowDetails: (resource: Resource) => void;
}

const ResourceContentRow: React.FC<ResourceContentRowProps> = ({ resource, tags, tagMap, onShowDetails }) => {
  const [showTagsModal, setShowTagsModal] = useState(false);
  const { updateResource } = useUpdateResource(resource.id!);

  const tagNames = useMemo(() => getResourceTagNames(resource, tagMap), [resource, tagMap]);

  const handleSaveTags = async (selectedTagIds: number[]) => {
    try {
      await updateResource.mutateAsync({ tag_ids: selectedTagIds });
      setShowTagsModal(false);
    } catch {
      /* mutation toast handles error */
    }
  };

  return (
    <>
      <tr className="text-gray-dark text-sm">
        <td className="px-4 py-4 align-top">
          <Truncate title={resource.name} className="font-semibold">
            {resource.name}
          </Truncate>
          <Truncate title={resource.url} className="mt-0.5 text-xs text-gray-500">
            {resource.url}
          </Truncate>
        </td>
        <td className="px-4 py-4">
          {resource.client_id ? (
            <div
              className="min-w-0 truncate rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700"
              title={`${resource.client_id}: ${resource.client_name}`}
            >
              {resource.client_id}: {resource.client_name}
            </div>
          ) : (
            <span className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">Global</span>
          )}
        </td>
        <td className="px-4 py-4 text-sm">{formatEnabledFor(resource.enabled_for, resource.client_id)}</td>
        <td className="px-4 py-4 capitalize">{resource.status}</td>
        <td className="px-4 py-4">{resource.sort_weight}</td>
        <td className="px-4 py-4">
          <div className="flex items-center justify-center gap-2">
            <button
              className="bg-blue-primary rounded-md px-4 py-2 text-xs font-semibold text-white"
              onClick={() => setShowTagsModal(true)}
              disabled={updateResource.isLoading}
            >
              Manage Tags
            </button>
            <button
              className="rounded-md border border-gray-300 px-4 py-2 text-xs font-semibold text-gray-700"
              onClick={() => onShowDetails(resource)}
            >
              View Details
            </button>
          </div>
        </td>
      </tr>
      <tr>
        <td colSpan={6} className="border-b-2 border-gray-200 px-4 py-3">
          <TagList tags={tagNames} />
        </td>
      </tr>
      {showTagsModal && (
        <TagSelectorModal
          title="Select Tags"
          tags={tags}
          selectedTagIds={resource.tag_ids}
          onSave={handleSaveTags}
          onClose={() => setShowTagsModal(false)}
        />
      )}
    </>
  );
};
